import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Stack,
  Typography,
  Divider,
  IconButton,
  useScrollTrigger,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HomeIcon from '@mui/icons-material/Home';
import { useTranslation } from 'react-i18next';
import { ReactComponent as MyHealthLogo } from '../graphics/myhealth-logo-white.svg';
import appConfig from '../config/appConfig';
import DisclaimerModal from './DisclaimerModal';
import displayConfig from '../config/displayConfig';
import { getByDisplayKey } from '../utils/displayKeyUtil';
import IDKeys from '../constants/idKeys';
import { formatDate } from '../utils/momentUtil';
import ViewerLink from './ViewerLink';
import { getViewerUrl } from '../routing/sectionUrl';

/**
 * The default header for the application.
 * @param {object} props Props accepted by the component.
 * @param {string} props.homeURL When the home icon is clicked, navigate to this URL.
 * @param {object} props.ps The PS we are viewing.
 * @returns {React.FunctionComponent} React element that contains the header.
 */
function PageHeader({ homeURL, ps }) {
  const [translate, i18n] = useTranslation();
  const internalHomeURL = homeURL || `/?lang=${i18n.language}`;
  const trigger = useScrollTrigger({
    disableHysteresis: true,
  });
  const [disclaimerOpen, setDisclaimerOpen] = useState(false);

  // Get composition resource from the bundle
  const compositionConfig = displayConfig[appConfig.spec].composition;
  const compositionResource =
    ps?.entry.find(
      (e) => e.resource.resourceType === compositionConfig.resourceType,
    ) || {};

  const date = getByDisplayKey(compositionResource, compositionConfig.dateKey);

  return (
    <Box role="banner">
      <DisclaimerModal
        open={disclaimerOpen}
        closeModal={() => setDisclaimerOpen(false)}
      />
      <AppBar
        position="sticky"
        elevation={trigger ? 4 : 0}
        sx={{ alignItems: 'center' }}
      >
        <Box
          maxWidth="sm"
          sx={{
            textAlign: 'left',
            width: 'stretch',
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            p: 1,
          }}
        >
          <ViewerLink
            id={`${IDKeys.HEADER}HomeLink`}
            aria-label={translate('pageHeader.bannerLinkLabel')}
            to={internalHomeURL}
            href={getViewerUrl({ language: i18n.language })}
            style={{
              textDecoration: 'none',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {appConfig.displayHeaderHomeIcon && (
              <HomeIcon
                id={`${IDKeys.HEADER}HomeIcon`}
                fontSize="large"
                htmlColor="white"
              />
            )}
            <MyHealthLogo
              id={`${IDKeys.HEADER}MyHealthNBLogo`}
              width="115"
              height="46"
            />
          </ViewerLink>
          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ mx: 1, borderColor: 'white' }}
          />
          <Stack
            sx={{
              width: '100%',
              paddingY: 1,
            }}
          >
            <Typography id={`${IDKeys.HEADER}TitleText`} variant="h_sm">
              {translate('psTitle')}
            </Typography>
            {appConfig.displayHeaderDate && date && (
              <Typography
                id={`${IDKeys.HEADER}DateText`}
                variant="body_xs"
                sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
              >
                {`${translate('asOfSubtitle')} ${formatDate(date)}`}
              </Typography>
            )}
          </Stack>
          <IconButton
            id={`${IDKeys.HEADER}DisclaimerButton`}
            aria-label={translate('pageHeader.disclaimerButtonLabel')}
            onClick={() => setDisclaimerOpen(true)}
          >
            <InfoOutlinedIcon sx={{ color: 'white' }} />
          </IconButton>
        </Box>
      </AppBar>
    </Box>
  );
}

export default PageHeader;
